/**
 * Eagerly warm play shell (play.js + play.css) on the publisher page so the
 * overlay iframe reuses a single preloaded copy (no duplicate fetch + script).
 */

const _shellWarmByHost: Partial<Record<string, Promise<void>>> = {};

/**
 * Must match `playJsCacheBustToken()` (scripts/vite/define.mjs) / player.html `?v=` query.
 */
export function playJsCacheBustQuery(): string {
  if (typeof window !== "undefined" && window.PIXMOTO_PLAY_JS_VERSION) {
    return `?v=${encodeURIComponent(String(window.PIXMOTO_PLAY_JS_VERSION))}`;
  }
  // A build-time define (scripts/vite/define.mjs), so the bundle holds no `process` shim.
  if (process.env.PLAY_JS_CACHE_BUST) {
    return `?v=${encodeURIComponent(String(process.env.PLAY_JS_CACHE_BUST))}`;
  }
  return "";
}

export function getPlayShellAssetUrls(playHost?: string | null): {
  js: string;
  css: string;
  config: string;
  boot: string;
} {
  const proto =
    typeof location !== "undefined" && location.protocol
      ? location.protocol
      : "https:";
  const host = playHost || "play.pixmoto.com";
  const base = `${proto}//${host}`;
  const versionQuery = playJsCacheBustQuery();
  return {
    js: `${base}/js/play.js${versionQuery}`,
    css: `${base}/css/play.css${versionQuery}`,
    config: `${base}/config.js`,
    boot: `${base}/js/player-boot.js${versionQuery}`,
  };
}

function _preloadLinkId(url: string): string {
  return (
    "pix-preload-" +
    String(url)
      .replace(/[^a-z0-9]/gi, "-")
      .slice(0, 72)
  );
}

/**
 * One <link rel="preload"> per URL — consumed later by iframe script/stylesheet.
 * Do NOT also fetch(): that duplicates the full 1.6MB download.
 */
function _injectPreloadLink(url: string, as: string): Promise<void> {
  if (!url || typeof document === "undefined") {
    return Promise.resolve();
  }
  const id = _preloadLinkId(url);
  const existing = document.getElementById(id);
  if (existing) {
    return Promise.resolve();
  }

  return new Promise<void>(function (resolve) {
    const link = document.createElement("link");
    link.id = id;
    link.rel = "preload";
    link.as = as;
    link.href = url;
    link.onload = function () {
      resolve();
    };
    link.onerror = function () {
      resolve();
    };
    document.head.appendChild(link);
  });
}

/**
 * Preload play bundle once per play host (versioned play.js URL).
 * @param {string} playHost
 * @returns {Promise<void>}
 */
export function warmPlayShell(
  playHost: string | null | undefined,
): Promise<void> {
  if (typeof window === "undefined" || !playHost) {
    return Promise.resolve();
  }

  if (_shellWarmByHost[playHost]) {
    return _shellWarmByHost[playHost];
  }

  const assets = getPlayShellAssetUrls(playHost);
  // <link rel="preload"> fills HTTP cache for iframe script/css loads (no extra fetch()).
  const warm = Promise.all([
    _injectPreloadLink(assets.css, "style"),
    _injectPreloadLink(assets.config, "script"),
    _injectPreloadLink(assets.boot, "script"),
    // Not modulepreload: Firefox caches its anonymous CORS fetch apart from the iframe's same-origin module load.
    _injectPreloadLink(assets.js, "script"),
  ]).then(function () {
    /* resolve with undefined */
  });
  _shellWarmByHost[playHost] = warm;

  return warm;
}
